import { nameValuePropType } from '@deltek/specification-client/core/Models/nameValuePropType'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import Button from '@material-ui/core/Button'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogTitle from '@material-ui/core/DialogTitle'
import { makeStyles, Theme } from '@material-ui/core/styles'
import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'
import Autocomplete, {
    AutocompleteChangeReason,
    AutocompleteInputChangeReason,
} from '@material-ui/lab/Autocomplete'
import React from 'react'

const useStyles = makeStyles((theme: Theme) => ({
    dialogTitle: {
        '& .MuiTypography-h6': {
            color: '#304959',
            fontSize: pxToRem(34),
        },
        '&.MuiDialogTitle-root': {
            paddingLeft: theme.spacing(0),
            paddingRight: theme.spacing(0),
        },
    },
    actionButton: {
        color: '#A78966',
        fontSize: pxToRem(18),
        width: 108,
        marginBottom: 16,
    },
    promptText: {
        paddingTop: theme.spacing(1),
        fontSize: pxToRem(14),
        color: theme.palette.common.black,
    },
    textField: {
        width: pxToRem(450),
    },
    dialog: {
        '& .MuiDialog-paperScrollPaper': {
            paddingLeft: theme.spacing(3),
            paddingRight: theme.spacing(3),
        },
    },
    dialogAction: {
        '&.MuiDialogActions-root': {
            paddingLeft: theme.spacing(0),
            paddingRight: theme.spacing(0),
        },
    },
}))

export interface AddSustainabilityDialogProps {
    /**
     * Determines whether dialog is open.
     */
    openDialog: boolean

    /**
     * Callback when dialog is cancelled
     */
    handleCancel?: VoidFunction

    /**
     * Callback when dialog is OK'ed
     */
    handleOK?: (
        value: Record<nameValuePropType, string>[],
        searchedText: string
    ) => void

    /**
     * Text for the dialog title.
     */
    dialogHeading?: string

    /**
     * Autocomplete options.
     */
    options: Record<nameValuePropType, string>[]

    /**
     * loading options.
     */
    loadingOptions?: boolean

    /*
     * Defines the prompt placed above the input textbox.
     */
    inputPrompt?: string

    /*
     * Defines the hint/label for the input textbox.
     */
    dropDownLabel?: string

    /*
     * Function for searching input text.
     */
    handleSearchInput?: (value: string) => void

    /*
     * Defines the text for cancel action button on confirmation dialog. Default: Cancel
     */
    cancelText?: string

    /*
     * Defines the text for confirmation action button  on confirmation dialog. Default: OK
     */
    okText?: string
}
const AddSustainabilityTopicDialog: React.FC<AddSustainabilityDialogProps> = ({
    openDialog,
    handleCancel,
    handleOK,
    dialogHeading,
    options,
    loadingOptions,
    inputPrompt,
    dropDownLabel,
    handleSearchInput,
    cancelText = 'Cancel',
    okText = 'Insert',
}) => {
    const classes = useStyles()

    const [searchedText, setsearchedText] = React.useState<string>('')

    const [selectedTopic, setSelectedTopic] = React.useState<
        Record<nameValuePropType, string>[]
    >()

    const handleOKClick = React.useCallback(() => {
        if (selectedTopic || searchedText) {
            handleOK?.(selectedTopic!, searchedText)
        }
    }, [handleOK, selectedTopic, searchedText])

    const handleCancelClick = React.useCallback(() => {
        handleCancel?.()
    }, [handleCancel])

    // New Code
    const handleInputChange = (
        _event: React.ChangeEvent<{}>,
        value: string,
        reason: AutocompleteInputChangeReason
    ) => {
        if (reason !== 'reset') {
            handleSearchInput?.(value)
            setsearchedText(value)
        }
    }
    const handleOpen = () => {
        handleSearchInput?.(searchedText)
    }

    const handleChange = (
        event: React.ChangeEvent<{}>,
        values: Record<nameValuePropType, string>[] | null,
        reason: AutocompleteChangeReason
    ) => {
        if (values) {
            setSelectedTopic(reason !== 'clear' ? values : undefined)
        }
    }

    return (
        <Dialog
            open={openDialog}
            data-testid="AddSustainabilityTopicDialog"
            className={classes.dialog}
        >
            <DialogTitle className={classes.dialogTitle}>
                {dialogHeading}
            </DialogTitle>
            <Typography className={classes.promptText}>
                {inputPrompt}
            </Typography>
            <Autocomplete
                data-testid="AddSustainabilityAutocomplete"
                multiple
                options={options}
                clearOnBlur={false}
                ListboxProps={{ 'data-testid': 'options' }}
                getOptionLabel={(option) => `${option.value}`}
                getOptionSelected={(option, value) =>
                    option?.value === value?.value
                }
                className={classes.textField}
                onInputChange={handleInputChange}
                onChange={handleChange}
                loading={loadingOptions}
                onOpen={handleOpen}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={dropDownLabel}
                        variant="outlined"
                        data-testid="AddSustainabilityTextField"
                    />
                )}
            />

            <DialogActions className={classes.dialogAction}>
                <Button
                    data-testid="AddSustainabilityDialogCancelButton"
                    className={classes.actionButton}
                    onClick={handleCancelClick}
                >
                    {cancelText}
                </Button>
                <Button
                    data-testid="AddSustainabilityDialogOKButton"
                    onClick={handleOKClick}
                    className={classes.actionButton}
                    disabled={selectedTopic == null && searchedText === ''}
                >
                    {okText}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default AddSustainabilityTopicDialog
